/**
 * React hook wrapping UploadQueue with reactive state.
 *
 * Usage:
 *   const { items, addFiles, startUpload, summary, ... } = useUploadQueue();
 *
 *   // User drops files
 *   const result = addFiles(fileList);
 *   // result.rejected contains files that failed validation
 *
 *   // User clicks upload
 *   startUpload();
 *
 *   // Render items with progress, retry failed, etc.
 */
import { useCallback, useReducer, useRef } from "react";
import { UploadQueue } from "../utils/uploadQueue";
import {
  type FileUploadItem,
  type UploadQueueOptions,
  type UploadQueueSummary,
  type AddFilesResult,
} from "../types/attachment";

// --- Reducer ---

type State = {
  items: FileUploadItem[];
  summary: UploadQueueSummary;
};

type Action =
  | { type: "SYNC"; items: FileUploadItem[]; summary: UploadQueueSummary };

const initialState: State = {
  items: [],
  summary: { total: 0, queued: 0, uploading: 0, uploaded: 0, failed: 0 },
};

function reducer(_state: State, action: Action): State {
  switch (action.type) {
    case "SYNC":
      return { items: action.items, summary: action.summary };
    default:
      return _state;
  }
}

// --- Hook ---

type UseUploadQueueOptions = Omit<UploadQueueOptions, "onItemUpdate">;

export function useUploadQueue(options?: UseUploadQueueOptions) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Sync React state from queue on every item change
  const syncFromQueue = useCallback((queue: UploadQueue) => {
    dispatch({
      type: "SYNC",
      items: queue.getItems(),
      summary: queue.getSummary(),
    });
  }, []);

  // Stable queue instance — persists across re-renders
  const queueRef = useRef<UploadQueue | null>(null);
  if (!queueRef.current) {
    queueRef.current = new UploadQueue({
      ...options,
      onItemUpdate: () => syncFromQueue(queueRef.current!),
    });
  }

  const addFiles = useCallback((files: File[]): AddFilesResult => {
    const result = queueRef.current!.add(files);
    syncFromQueue(queueRef.current!);
    return result;
  }, [syncFromQueue]);

  const startUpload = useCallback((): void => {
    queueRef.current!.start();
  }, []);

  const retryFile = useCallback((index: number): void => {
    queueRef.current!.retry(index);
  }, []);

  const retryAllFailed = useCallback((): void => {
    queueRef.current!.retryAllFailed();
  }, []);

  const cancelFile = useCallback((index: number): void => {
    queueRef.current!.cancel(index);
    syncFromQueue(queueRef.current!);
  }, [syncFromQueue]);

  const cancelAll = useCallback((): void => {
    queueRef.current!.cancelAll();
    syncFromQueue(queueRef.current!);
  }, [syncFromQueue]);

  const reset = useCallback((): void => {
    queueRef.current!.cancelAll();
    queueRef.current = new UploadQueue({
      ...options,
      onItemUpdate: () => syncFromQueue(queueRef.current!),
    });
    dispatch({ type: "SYNC", items: [], summary: initialState.summary });
  }, [options, syncFromQueue]);

  const isUploading = state.summary.uploading > 0 || state.summary.queued > 0;

  return {
    items: state.items,
    summary: state.summary,
    isUploading,
    addFiles,
    startUpload,
    retryFile,
    retryAllFailed,
    cancelFile,
    cancelAll,
    reset,
  };
}
